// DPCA (Dialisis Peritoneal Continua Ambulatoria) section for the care program.
// Renders a table of exchanges + morning weight + shift totals + alert banner.

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, AlertTriangle, Droplet, Send } from "lucide-react";
import { toast } from "sonner";
import {
  APPEARANCE_LABELS,
  CONCENTRATION_LABELS,
  CONCENTRATION_COLORS,
  exchangeUF,
  summarizeDialysisShift,
  detectDialysisCriticalReasons,
} from "@/lib/dialysis-alerts";
import {
  getSupervisorPhone,
  openSupervisorWhatsApp,
} from "@/lib/critical-alert";

function toIntOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(String(v).replace(/[^0-9-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

// Short, focused WhatsApp message for a single non-clear DPCA exchange.
// Kept short so it fits a single WhatsApp bubble preview.
function buildTurbidAlertMessage({ patientName, address, nurseName, exchange }) {
  const lines = [];
  lines.push("ALERTA DPCA - Ciudad Enfermeria");
  lines.push("");
  lines.push(`Paciente: ${patientName || "-"}`);
  if (address) lines.push(`Direccion: ${address}`);
  if (nurseName) lines.push(`Enfermera: ${nurseName}`);
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  lines.push(`Hora del aviso: ${hh}:${mm}`);
  lines.push("");
  const label = APPEARANCE_LABELS[exchange.appearance] || exchange.appearance;
  lines.push(`Recambio #${exchange.number || "?"} (${exchange.start_time || "--:--"})`);
  lines.push(`Bolsa: ${CONCENTRATION_LABELS[exchange.concentration] || exchange.concentration || "-"}`);
  lines.push(`Aspecto del liquido: ${label}`);
  if (exchange.infused_ml || exchange.drained_ml) {
    lines.push(`Vol: ${exchange.infused_ml || "-"} / ${exchange.drained_ml || "-"} ml`);
  }
  if (exchange.notes) lines.push(`Notas: ${exchange.notes}`);
  lines.push("");
  lines.push("SOSPECHA DE PERITONITIS - Requiere valoracion medica inmediata.");
  return lines.join("\n");
}

export default function PeritonealDialysisSection({
  pd,
  onChange,
  defaultInfusedMl = 2000,
  patientName = "",
  patientAddress = "",
  nurseName = "",
}) {
  const data = pd || { exchanges: [], morning_weight_kg: "", observations: "" };
  const exchanges = Array.isArray(data.exchanges) ? data.exchanges : [];
  const totals = summarizeDialysisShift(data);
  const reasons = detectDialysisCriticalReasons(data);

  const addRow = () => {
    const next = [
      ...exchanges,
      {
        id: `pd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        number: exchanges.length + 1,
        start_time: "",
        concentration: "",
        infused_ml: defaultInfusedMl,
        drained_ml: null,
        appearance: "claro",
        notes: "",
      },
    ];
    onChange({ ...data, exchanges: next });
  };

  const updateRow = (idx, key, value) => {
    const prev = exchanges[idx] || {};
    const nextRow = { ...prev, [key]: value };

    // Auto-alert: appearance changed to a non-clear value and this specific
    // exchange hasn't been alerted yet in this program. Fires WhatsApp
    // synchronously so the browser treats it as a user gesture.
    const shouldAlert =
      key === "appearance" && value && value !== "claro" && !prev.alerted_at;
    if (shouldAlert) {
      nextRow.alerted_at = new Date().toISOString();
      try {
        const phone = getSupervisorPhone();
        const msg = buildTurbidAlertMessage({
          patientName,
          address: patientAddress,
          nurseName,
          exchange: nextRow,
        });
        const opened = openSupervisorWhatsApp(msg, phone);
        if (opened) {
          toast.warning(
            phone
              ? "Alerta enviada al supervisor por WhatsApp"
              : "Se abrio WhatsApp - selecciona al supervisor",
            { duration: 5000 }
          );
        } else {
          toast.error(
            "El navegador bloqueo la ventana. Usa el boton verde de WhatsApp abajo.",
            { duration: 6000 }
          );
        }
      } catch (err) {
        console.warn("Auto WhatsApp alert failed", err);
      }
    }

    const next = exchanges.map((e, i) => (i === idx ? nextRow : e));
    onChange({ ...data, exchanges: next });
  };

  const removeRow = (idx) => {
    const next = exchanges.filter((_, i) => i !== idx).map((e, i) => ({ ...e, number: i + 1 }));
    onChange({ ...data, exchanges: next });
  };

  const ufSign = (n) => (n > 0 ? `+${n}` : `${n}`);
  const ufColor = (n) => {
    if (n === null) return "text-slate-400";
    if (n < -500) return "text-red-700";
    if (n > 500) return "text-emerald-700";
    return "text-slate-700";
  };

  return (
    <div className="space-y-4" data-testid="dialysis-section">
      {/* Header row: peso corporal matutino */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <Label className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500">
            <Droplet className="h-3 w-3" /> Peso matutino (kg)
          </Label>
          <Input
            type="text"
            inputMode="decimal"
            value={data.morning_weight_kg || ""}
            onChange={(e) => onChange({ ...data, morning_weight_kg: e.target.value })}
            placeholder="72.4"
            className="h-10 text-base"
            data-testid="dialysis-morning-weight"
          />
          <p className="mt-0.5 text-[10px] italic text-slate-500">
            En ayunas, vejiga vacia, cavidad peritoneal vacia.
          </p>
        </div>
      </div>

      {/* Alert banner if any critical finding */}
      {reasons.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800"
          data-testid="dialysis-alert-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-bold">Hallazgo critico DPCA:</p>
            <ul className="mt-0.5 list-inside list-disc text-[13px]">
              {reasons.slice(0, 4).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
              {reasons.length > 4 && <li>+{reasons.length - 4} mas</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Exchanges table (card-per-row on mobile, table-like on desktop) */}
      {exchanges.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white px-4 py-8 text-center">
          <p className="text-sm font-semibold text-slate-700">Sin recambios registrados</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Toca &quot;Agregar recambio&quot; para iniciar la bitacora del turno.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {exchanges.map((ex, idx) => {
          const uf = exchangeUF(ex);
          const concCfg = CONCENTRATION_COLORS[ex.concentration] || CONCENTRATION_COLORS.otra;
          const isCritical =
            (ex.appearance && ex.appearance !== "claro") ||
            (uf !== null && uf < -500) ||
            (ex.drained_ml && Number(ex.drained_ml) < 1500 && Number(ex.infused_ml) >= 1500);
          return (
            <div
              key={ex.id || idx}
              className={`rounded-xl border p-3 shadow-sm ${
                isCritical ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-white"
              }`}
              data-testid={`dialysis-row-${idx}`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: concCfg.dot }}
                >
                  {ex.number || idx + 1}
                </span>
                {ex.alerted_at && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800"
                    data-testid={`dialysis-alerted-${idx}`}
                    title={`Alerta WhatsApp enviada ${new Date(ex.alerted_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}`}
                  >
                    <Send className="h-3 w-3" /> Supervisor avisado
                  </span>
                )}
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-red-100"
                  onClick={() => removeRow(idx)}
                  data-testid={`dialysis-remove-${idx}`}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Hora</Label>
                  <Input
                    type="time"
                    value={ex.start_time || ""}
                    onChange={(e) => updateRow(idx, "start_time", e.target.value)}
                    className="h-9 text-sm"
                    data-testid={`dialysis-time-${idx}`}
                  />
                </div>
                <div className="col-span-2 sm:col-span-2">
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Bolsa</Label>
                  <Select
                    value={ex.concentration || ""}
                    onValueChange={(v) => updateRow(idx, "concentration", v)}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid={`dialysis-conc-${idx}`}>
                      <SelectValue placeholder="Concentracion" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CONCENTRATION_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: CONCENTRATION_COLORS[k].dot }}
                            />
                            {label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Infundido ml</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={ex.infused_ml === "" || ex.infused_ml == null ? "" : ex.infused_ml}
                    onChange={(e) => updateRow(idx, "infused_ml", toIntOrNull(e.target.value))}
                    className="h-9 text-sm"
                    placeholder="2000"
                    data-testid={`dialysis-infused-${idx}`}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Drenado ml</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={ex.drained_ml === "" || ex.drained_ml == null ? "" : ex.drained_ml}
                    onChange={(e) => updateRow(idx, "drained_ml", toIntOrNull(e.target.value))}
                    className="h-9 text-sm"
                    placeholder="2150"
                    data-testid={`dialysis-drained-${idx}`}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">UF</Label>
                  <div
                    className={`flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-2 text-sm font-bold ${ufColor(uf)}`}
                    data-testid={`dialysis-uf-${idx}`}
                  >
                    {uf === null ? "--" : ufSign(uf)}
                  </div>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                    Aspecto del liquido
                  </Label>
                  <Select
                    value={ex.appearance || "claro"}
                    onValueChange={(v) => updateRow(idx, "appearance", v)}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid={`dialysis-appearance-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(APPEARANCE_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Notas</Label>
                  <Input
                    type="text"
                    value={ex.notes || ""}
                    onChange={(e) => updateRow(idx, "notes", e.target.value)}
                    placeholder="Fuga, dolor abdominal, permanencia..."
                    className="h-9 text-sm"
                    data-testid={`dialysis-notes-${idx}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addRow}
        className="h-11 w-full rounded-xl border-dashed border-slate-300 text-sm font-semibold text-slate-700 hover:border-[#1D4ED8] hover:bg-blue-50"
        data-testid="dialysis-add-row"
      >
        <Plus className="mr-1 h-4 w-4" /> Agregar recambio
      </Button>

      {/* Shift totals */}
      {exchanges.length > 0 && (
        <div
          className="rounded-xl border border-slate-200 bg-slate-50 p-3"
          data-testid="dialysis-shift-totals"
        >
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            Subtotal del turno
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Recambios</div>
              <div className="font-mono text-base font-bold text-slate-900">{totals.count}</div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Infundido</div>
              <div className="font-mono text-base font-bold text-slate-900">
                {totals.infused_ml} ml
              </div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Drenado</div>
              <div className="font-mono text-base font-bold text-slate-900">
                {totals.drained_ml} ml
              </div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">UF del turno</div>
              <div
                className={`font-mono text-base font-bold ${ufColor(totals.ultrafiltration_ml)}`}
                data-testid="dialysis-shift-uf"
              >
                {ufSign(totals.ultrafiltration_ml)} ml
              </div>
            </div>
          </div>
          <p className="mt-2 text-[11px] italic text-slate-500">
            El balance de 24h se calcula en el resumen del paciente sumando todos los turnos del dia.
          </p>
        </div>
      )}

      {/* Free-text observations for the shift */}
      <div>
        <Label className="mb-1 block text-xs font-bold uppercase text-slate-500">
          Observaciones del turno (DPCA)
        </Label>
        <textarea
          value={data.observations || ""}
          onChange={(e) => onChange({ ...data, observations: e.target.value })}
          rows={2}
          placeholder="Molestias, fugas, dificultad para infundir/drenar, aspecto del sitio de salida..."
          className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]"
          data-testid="dialysis-observations"
        />
      </div>
    </div>
  );
}
